import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { Banner, Box, Frame } from '@shopify/polaris';

import { useScrollTopOnNavigate } from '@/hooks/useScrollTopOnNavigate';
import { isPreview, useSession } from '@/providers/session';
import { useConfigStore, useLoadConfig, useSeedConfig } from '@/state/config';
import { isStandaloneMode } from '@/utils/standalone';

import { AppNavMenu } from './AppNavMenu';
import { SaveBar } from './SaveBar';
import { StandaloneNav } from './StandaloneNav';

/**
 * The shell.
 *
 * Owns three things every page depends on and none of them should own:
 *   1. loading the config exactly once, against the session's installation id;
 *   2. the App Bridge contextual save bar, which is global to the app frame;
 *   3. the persistent error Banner;
 *   4. landing each navigation at the top of the new page.
 *
 * ⚠ The Banner is not a toast and must never become one. App Bridge toasts time
 * out, and Built for Shopify §4.2.4 lists an error that disappears on a timer as
 * a rejection reason. Toasts are for success only.
 */
export function AppLayout() {
  /**
   * ⚠ Here because this is the one layout BOTH modes render — embedded and
   * standalone — and it is mounted once for the life of the app, so the "was
   * this the first render?" ref it keeps is not reset by a page changing.
   * Putting it in a page would reset that ref on every navigation, which is
   * precisely the state it exists to remember. See the hook.
   */
  useScrollTopOnNavigate();
  const { t } = useTranslation();
  const session = useSession();
  const load = useLoadConfig();
  const seed = useSeedConfig();
  const { error, setError, config } = useConfigStore();

  useEffect(() => {
    if (!session) return;
    // ⚠ The zone travels WITH the load. Shopify knows the store's clock and the
    // config does not; passing it here is the only write `settings.timezone`
    // has ever had, and without it the admin prints one zone and bakes another
    // (`withStoreTimezone`).
    if (isPreview()) seed(session.appInstallationId, session.timezone);
    else void load(session.appInstallationId, session.timezone);
  }, [session, load, seed]);

  return (
    <Frame>
      {/*
        ⚠ One or the other, never both. Embedded, App Bridge lifts
        `<ui-nav-menu>` out of the iframe into Shopify's own sidebar. Standalone
        has no sidebar to lift it into, so the app draws its own.
      */}
      {isStandaloneMode() ? <StandaloneNav /> : <AppNavMenu />}
      <SaveBar />
      {error && (
        <div style={{ padding: '12px 16px 0' }}>
          <Banner tone="critical" title={t('app.problem')} onDismiss={() => setError(null)}>
            {/* The store carries a KEY and its parameters; the sentence is made
                here, where there is a `t`. See `errorAtom`. */}
            <p>{t(error.key, error.params)}</p>
          </Banner>
        </div>
      )}
      {/*
        Pages render their own skeletons; the config being null is a normal
        first-paint state, not an error.

        ── The tail, and why it is here rather than on twelve pages ──────────

        Polaris `Page` reserves nothing after its last card, so the final
        element on every surface — the "Missing an effect or an occasion?" cell —
        sat flush against the bottom edge of the viewport, reading as a page
        that had been cut off rather than one that had ended. One wrapper fixes
        it for every route at once; twelve copies of the same padding is twelve
        chances for a new page to be the one that forgets.

        ⚠ It does NOT fight the standalone save bar, and it must not try to.
        `utils/standalone.ts` pads the BODY (`html.cf-savebar-open body`) so the
        last field can be scrolled clear of a floating bar that would otherwise
        swallow clicks. That padding is on a different box, is conditional on
        the bar being open, and is measured against the bar's own height — the
        two simply stack, and stacking is the right answer: the failure mode of
        a little too much room at the end of a page is nothing at all, while too
        little is a control a merchant cannot reach.
      */}
      {config !== undefined && (
        <Box paddingBlockEnd="800">
          <Outlet />
        </Box>
      )}
    </Frame>
  );
}
